import React, { useEffect, useState, useRef } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Fab,
    CircularProgress,
    Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { isAdminMode } from '../utils/auth';

interface StaffMessage {
    id: number;
    name: string;
    position: string;
    message: string;
    imageSrc?: string;
}

interface StaffMessageFormData {
    name: string;
    position: string;
    message: string;
    imageSrc: string;
}

interface StaffMessageProps {
    messages?: StaffMessage[];
}

const MessageCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
    },
    position: 'relative'
}));

const StaffImage = styled('img')({
    width: '100%',
    height: '240px',
    objectFit: 'cover',
    objectPosition: 'center top',
});

const MessageContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(3),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
}));

const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const StaffMessages: React.FC<StaffMessageProps> = ({ messages: initialMessages = [] }) => {
    const theme = useTheme();
    const [messages, setMessages] = useState<StaffMessage[]>(initialMessages || []);
    const [loading, setLoading] = useState(!initialMessages);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMessage, setEditingMessage] = useState<StaffMessage | null>(null);
    const [formData, setFormData] = useState<StaffMessageFormData>({
        name: '',
        position: '',
        message: '',
        imageSrc: ''
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!initialMessages) {
            fetchMessages();
        }
    }, [initialMessages]);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/api/StaffMessages`);
            if (!response.ok) throw new Error('Failed to fetch messages');
            const data = await response.json();
            setMessages(data);
        } catch (err: any) {
            setError(err.message);
            console.error('Failed to fetch messages:', err);
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditingMessage(null);
        setFormData({
            name: '',
            position: '',
            message: '',
            imageSrc: ''
        });
        setImageFile(null);
        setImagePreview('');
        setDialogOpen(true);
    };

    const openEditDialog = (message: StaffMessage) => {
        setEditingMessage(message);
        setFormData({
            name: message.name,
            position: message.position,
            message: message.message,
            imageSrc: message.imageSrc || ''
        });
        setImageFile(null);
        // Set preview from existing image
        if (message.imageSrc) {
            if (message.imageSrc.startsWith('http') || message.imageSrc.startsWith('/')) {
                setImagePreview(message.imageSrc);
            } else {
                setImagePreview(`${BASE_URL}/public/StaffImages/${message.imageSrc}`);
            }
        } else {
            setImagePreview('');
        }
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingMessage(null);
        setFormData({
            name: '',
            position: '',
            message: '',
            imageSrc: ''
        });
        setImageFile(null);
        setImagePreview('');
    };

    const handleFormChange = (field: keyof StaffMessageFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only JPG, PNG, and GIF images are allowed');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size exceeds 5MB limit');
                return;
            }

            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const uploadImage = async (): Promise<string> => {
        if (!imageFile) return formData.imageSrc;

        try {
            setUploading(true);
            const uploadFormData = new FormData();
            uploadFormData.append('image', imageFile);

            const response = await fetch(`${BASE_URL}/api/StaffMessages/upload`, {
                method: 'POST',
                body: uploadFormData
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed: ${errorText}`);
            }

            const result = await response.json();
            return result.fileName; // Return only the filename for storage
        } catch (error) {
            console.error('Image upload failed:', error);
            throw error;
        } finally {
            setUploading(false);
        }
    };

    const createMessage = async () => {
        try {
            let imageFilename = formData.imageSrc;

            // Upload new image if selected
            if (imageFile) {
                imageFilename = await uploadImage();
            }

            const messageData = {
                name: formData.name,
                position: formData.position,
                message: formData.message,
                imageSrc: imageFilename || undefined
            };

            const response = await fetch(`${BASE_URL}/api/StaffMessages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(messageData)
            });

            if (!response.ok) throw new Error('Failed to create message');

            const createdMessage = await response.json();
            setMessages(prev => [...prev, createdMessage]);
            closeDialog();
        } catch (err) {
            console.error('Failed to create message:', err);
            setError('Failed to create message');
        }
    };

    const updateMessage = async () => {
        if (!editingMessage) return;

        try {
            let imageFilename = formData.imageSrc;

            // Upload new image if selected
            if (imageFile) {
                imageFilename = await uploadImage();
            }

            const messageData = {
                name: formData.name,
                position: formData.position,
                message: formData.message,
                imageSrc: imageFilename || undefined
            };

            const response = await fetch(`${BASE_URL}/api/StaffMessages/${editingMessage.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(messageData)
            });

            if (!response.ok) throw new Error('Failed to update message');

            const updatedMessage = await response.json();
            setMessages(prev =>
                prev.map(m => m.id === editingMessage.id ? updatedMessage : m)
            );
            closeDialog();
        } catch (err) {
            console.error('Failed to update message:', err);
            setError('Failed to update message');
        }
    };

    const deleteMessage = async (id: number) => {
        if (!confirm('Are you sure you want to delete this staff message?')) return;

        try {
            const response = await fetch(`${BASE_URL}/api/StaffMessages/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete message');

            setMessages(prev => prev.filter(m => m.id !== id));
        } catch (err) {
            console.error('Failed to delete message:', err);
            setError('Failed to delete message');
        }
    };

    const handleSave = async () => {
        if (editingMessage) {
            await updateMessage();
        } else {
            await createMessage();
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) return <div>Error: {error}</div>;

    return (
        <div className="min-w-screen  p-6">
            <div className="md:w-[80%] w-[90%] mx-auto">
                <Fade in timeout={800}>
                    <Box className="text-center mb-12">
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 3,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Staff Messages
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '1.1rem',
                                maxWidth: '600px',
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontWeight: 400
                            }}
                        >
                            Here you can add staff messages and view the messages.
                        </Typography>
                    </Box>
                </Fade>
                <div className="w-full flex flex-wrap gap-8 justify-center"
                >
                    {messages.length === 0 ? (
                        <Typography variant="body1" color="text.secondary">
                            No staff messages found
                        </Typography>
                    ) : (
                        messages.map((message) => (
                            <div
                                key={message.id}
                                className="w-full sm:w-[48%] md:w-[31%] mb-8"
                                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                            >
                                <MessageCard
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        bgcolor: 'background.paper',
                                        boxShadow: 3,
                                    }}
                                >
                                    {/* Admin Controls */}
                                    {isAdminMode() && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                display: 'flex',
                                                gap: 0.5,
                                                zIndex: 2
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => openEditDialog(message)}
                                                sx={{
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => deleteMessage(message.id)}
                                                sx={{
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,1)',
                                                        color: 'error.dark'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}

                                    {message.imageSrc ? (
                                        <StaffImage
                                            src={message.imageSrc.startsWith('http') || message.imageSrc.startsWith('/')
                                                ? message.imageSrc
                                                : `${BASE_URL}/public/StaffImages/${message.imageSrc}`}
                                            alt={message.name}
                                            onError={(e) => {
                                                // Fallback to default avatar if image fails to load
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: '240px',
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                fontSize: '3rem',
                                            }}
                                        >
                                            <PersonIcon sx={{ fontSize: '4rem' }} />
                                        </Box>
                                    )}

                                    <MessageContent>
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            color="primary"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.2rem',
                                                mb: 1
                                            }}
                                        >
                                            {message.name}
                                        </Typography>

                                        <Typography
                                            variant="subtitle1"
                                            color="text.secondary"
                                            sx={{
                                                fontStyle: 'italic',
                                                mb: 2
                                            }}
                                        >
                                            {message.position}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            color="text.primary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 4,
                                                WebkitBoxOrient: 'vertical',
                                                flexGrow: 1
                                            }}
                                        >
                                            {message.message}
                                        </Typography>
                                    </MessageContent>
                                </MessageCard>
                            </div>
                        ))
                    )}
                </div>

                {isAdminMode() && (
                    <Fab
                        color="primary"
                        aria-label="add"
                        onClick={openCreateDialog}
                        sx={{
                            position: 'relative',
                            bottom: theme.spacing(3),
                            right: theme.spacing(3),
                        }}
                    >
                        <AddIcon />
                    </Fab>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {editingMessage ? 'Edit Staff Message' : 'Create New Staff Message'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                margin="normal"
                            />

                            <TextField
                                label="Position"
                                fullWidth
                                value={formData.position}
                                onChange={(e) => handleFormChange('position', e.target.value)}
                                margin="normal"
                            />

                            <TextField
                                label="Message"
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.message}
                                onChange={(e) => handleFormChange('message', e.target.value)}
                                margin="normal"
                            />

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle1">Staff Photo</Typography>

                                {imagePreview ? (
                                    <Box sx={{ position: 'relative' }}>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{
                                                width: 200,
                                                height: 200,
                                                objectFit: 'cover',
                                                borderRadius: theme.shape.borderRadius,
                                                border: '1px solid #ddd'
                                            }}
                                        />
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,1)'
                                                }
                                            }}
                                            onClick={triggerFileInput}
                                        >
                                            <PhotoCameraIcon />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: 200,
                                            height: 200,
                                            border: '2px dashed #ccc',
                                            borderRadius: theme.shape.borderRadius,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main
                                            }
                                        }}
                                        onClick={triggerFileInput}
                                    >
                                        <PhotoCameraIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary" mt={1}>
                                            Upload Photo
                                        </Typography>
                                    </Box>
                                )}

                                <input
                                    type="file"
                                    hidden
                                    ref={fileInputRef}
                                    accept="image/jpeg, image/png, image/gif"
                                    onChange={handleImageSelect}
                                />

                                <Typography variant="caption" color="text.secondary">
                                    JPG, PNG or GIF (Max 5MB)
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {editingMessage ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default StaffMessages;